import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  TextField,
  Typography
} from "@mui/material";
import React, { useState } from "react";
import { t } from "ttag";
import Checkbox from "../../components/cool_widgets/CoolCheckbox";
import { IEntityRef, INewSubscription } from "../../models/DeviceHealth.types";
import useStyles from "./RegisterSiteUpdatesPopup.style";

/** Sentinel for the "all customer sites" option, mapped to a null site on submit. */
const ALL_SITES = "__all_sites__";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getOptionName = (option: IEntityRef) => option?.name || "";

const isSameOption = (option: IEntityRef, value: IEntityRef) => option?.id === value?.id;

interface IRegisterSiteUpdatesPopupProps {
  /** every customer in the system - not just those present in today's report */
  customers: IEntityRef[];
  loadingCustomers: boolean;
  /** loads the picked customer's sites on demand */
  loadCustomerSites: (customerId: string) => Promise<IEntityRef[]>;
  saving: boolean;
  onClose: () => void;
  onSubmit: (subscription: INewSubscription) => void;
}

const RegisterSiteUpdatesPopup: React.FC<IRegisterSiteUpdatesPopupProps> = ({
  customers,
  loadingCustomers,
  loadCustomerSites,
  saving,
  onClose,
  onSubmit
}) => {
  const classes: any = useStyles();

  const [customerId, setCustomerId] = useState<string>("");
  const [siteId, setSiteId] = useState<string>(ALL_SITES);
  const [siteOptions, setSiteOptions] = useState<IEntityRef[]>([]);
  const [loadingSites, setLoadingSites] = useState<boolean>(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState<string>("");
  const [incrementalUpdates, setIncrementalUpdates] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const fetchSites = async (nextCustomerId: string) => {
    setLoadingSites(true);

    try {
      setSiteOptions(await loadCustomerSites(nextCustomerId));
    } catch (fetchError: any) {
      setSiteOptions([]);
      setError(fetchError?.message || t`Failed to load the customer's sites.`);
    } finally {
      setLoadingSites(false);
    }
  };

  const handleCustomerChange = (_event: unknown, option: IEntityRef | null) => {
    setCustomerId(option?.id || "");
    setSiteId(ALL_SITES);
    setSiteOptions([]);
    setError("");

    if (option?.id) {
      fetchSites(option.id);
    }
  };

  const handleSiteChange = (_event: unknown, option: IEntityRef | null) => {
    setSiteId(option?.id || ALL_SITES);
  };

  /** "All sites" sits in the list so one searchable control covers both cases. */
  const siteChoices: IEntityRef[] = [{ id: ALL_SITES, name: t`All sites` }, ...siteOptions];

  const selectedCustomer = customers.find(({ id }) => id === customerId) || null;
  const selectedSite = siteChoices.find(({ id }) => id === siteId) || siteChoices[0];

  /**
   * Counts an address still sitting in the input, so the button enables as soon
   * as one is typed rather than only after it is committed to a chip.
   */
  const hasValidEmail =
    !!emails.length || EMAIL_PATTERN.test(emailDraft.trim().replace(/,$/, ""));

  const canSubmit = !!customerId && hasValidEmail;

  const handleIncrementalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncrementalUpdates(event.target.checked);
  };

  const handleEmailDraftChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailDraft(event.target.value);
    setError("");
  };

  /**
   * The address list including whatever is still sitting in the input, or null
   * when that pending text isn't a valid address. Kept pure so submit doesn't
   * have to race the setEmails state update.
   */
  const resolveEmails = (): string[] | null => {
    const candidate = emailDraft.trim().replace(/,$/, "");

    if (!candidate) {
      return emails;
    }

    if (!EMAIL_PATTERN.test(candidate)) {
      setError(t`"${candidate}" is not a valid email address.`);

      return null;
    }

    if (emails.includes(candidate)) {
      return emails;
    }

    return [...emails, candidate];
  };

  const commitEmailDraft = () => {
    const resolved = resolveEmails();

    if (!resolved) {
      return;
    }

    setEmails(resolved);
    setEmailDraft("");
  };

  const handleEmailKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== "," && event.key !== " ") {
      return;
    }

    event.preventDefault();
    commitEmailDraft();
  };

  const handleEmailBlur = () => {
    commitEmailDraft();
  };

  const handleRemoveEmail = (email: string) => () => {
    setEmails(emails.filter((current) => current !== email));
  };

  const handleSubmit = () => {
    const finalEmails = resolveEmails();

    if (!finalEmails) {
      return;
    }

    if (!customerId) {
      setError(t`Please select a customer.`);

      return;
    }

    if (!finalEmails.length) {
      setError(t`Please add at least one email address.`);

      return;
    }

    // Submitting with an address still in the input commits it to a chip first,
    // so it is visibly part of the list rather than silently swept into the call.
    setEmails(finalEmails);
    setEmailDraft("");

    // The API takes exactly one of siteId / customerId - a customerId expands to
    // every site that customer owns, which is what "All sites" means here.
    onSubmit(
      siteId === ALL_SITES
        ? { customerId, emails: finalEmails, diff: incrementalUpdates }
        : { siteId, emails: finalEmails, diff: incrementalUpdates }
    );
  };

  const renderEmailChips = () => {
    if (!emails.length) {
      return null;
    }

    return (
      <div className={classes.chipRow}>
        {emails.map((email) => (
          <Chip
            key={email}
            label={email}
            size="small"
            onDelete={handleRemoveEmail(email)}
            className={classes.chip}
          />
        ))}
      </div>
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.titleText}>{t`Register for Site Updates`}</Typography>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.description}>
          {t`Send device health reports for the selected site by email.`}
        </Typography>

        {/* Searchable: the lists span every customer and site in the system. */}
        <Autocomplete
          options={customers}
          value={selectedCustomer}
          onChange={handleCustomerChange}
          disabled={loadingCustomers}
          getOptionLabel={getOptionName}
          isOptionEqualToValue={isSameOption}
          className={classes.field}
          renderInput={(params: any) => (
            <TextField
              {...params}
              size="small"
              label={loadingCustomers ? t`Loading customers...` : t`Customer Name`}
              placeholder={t`Type to search...`}
            />
          )}
        />

        <Autocomplete
          options={siteChoices}
          value={selectedSite}
          onChange={handleSiteChange}
          disabled={!customerId || loadingSites}
          getOptionLabel={getOptionName}
          isOptionEqualToValue={isSameOption}
          className={classes.field}
          renderInput={(params: any) => (
            <TextField
              {...params}
              size="small"
              label={loadingSites ? t`Loading sites...` : t`Site Name`}
              placeholder={t`Type to search...`}
            />
          )}
        />

        <TextField
          fullWidth
          size="small"
          label={t`Email addresses`}
          placeholder={t`Type an address and press Enter`}
          value={emailDraft}
          onChange={handleEmailDraftChange}
          onKeyDown={handleEmailKeyDown}
          onBlur={handleEmailBlur}
          className={classes.field}
        />
        {renderEmailChips()}

        <FormControlLabel
          className={classes.checkboxLabel}
          control={<Checkbox checked={incrementalUpdates} onChange={handleIncrementalChange} />}
          label={
            <div>
              <Typography className={classes.checkboxTitle}>{t`Incremental updates`}</Typography>
              <Typography className={classes.checkboxHint}>
                {t`Send only what changed since the last report instead of the full daily report.`}
              </Typography>
            </div>
          }
        />

        {!!error && <FormHelperText error className={classes.error}>{error}</FormHelperText>}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} disabled={saving}>{t`Cancel`}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !canSubmit}>
          {saving ? <CircularProgress size={18} /> : t`Register`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterSiteUpdatesPopup;
